import { ArrayHelper } from "../../../ArrayHelper/static/impl/ArrayHelper";
import { ElementType } from "../../../Environment/enums/ElementType";
import { IFile } from "../../../FileStructure/interfaces/IFile";
import { DefaultFile } from "../../../FileStructure/model/impl/DefaultFile";
import { Constructor } from "../../../Numl/interfaces/Constructor";
import { ImportInformation } from "../../../Numl/interfaces/ImportInformation";
import { Method } from "../../../Numl/interfaces/Method";
import { Property } from "../../../Numl/interfaces/Property";
import { Type } from "../../../Type/interfaces/Type";
import { DefaultType } from "../../../Type/model/impl/DefaultType";
import { ClassExportInformation } from "../../interfaces/ClassExportInformation";
import { EnumExportInformation } from "../../interfaces/EnumExportInformation";
import { ExportInformation } from "../../interfaces/ExportInformation";
import { FileCreator2 } from "../../interfaces/FileCreator2";
import { InterfaceExpotInformation } from "../../interfaces/InterfaceExportInformation";
import { ViewExportInformation } from "../../interfaces/ViewExportInformation";

export class JavaFileCreator implements FileCreator2 {
  private fileExtension = ".java";

  constructor(private diagramName: string) {}

  CreateInterfaceFile(exportInformation: InterfaceExpotInformation): IFile {
    let content: string =
      "package " +
      this.diagramName.toLowerCase() +
      "." +
      exportInformation.VerticalDimension.toLowerCase() +
      ".interfaces;\n";
    content += this.stringifyImports(exportInformation.Imports);
    if (exportInformation.Expands.length === 0) {
      content += "\npublic interface " + exportInformation.Name + " {\n";
    } else {
      content +=
        "\npublic interface " +
        exportInformation.Name +
        " extends " +
        exportInformation.Expands.join(", ") +
        " {\n";
    }
    content += this.propsInterfaceBlock(exportInformation.Properties);
    content += this.stringifyInterfaceMethods(exportInformation.Methods);
    content += "\n}\n";

    return new DefaultFile(this.toFileName(exportInformation.Name), content);
  }
  CreateEnumFile(exportInformation: EnumExportInformation): IFile {
    let content: string = this.GetPackageString(exportInformation);
    content += "\npublic enum " + exportInformation.Name + " {\n\t";
    content += exportInformation.Values.join(",\n\t");
    content += "\n}\n";

    return new DefaultFile(this.toFileName(exportInformation.Name), content);
  }

  CreateClassFile(exportInformation: ClassExportInformation): IFile {
    let content: string = this.GetPackageString(exportInformation);
    content += this.stringifyImports(exportInformation.Imports);
    content +=
      "\nclass " +
      exportInformation.Constructors[0].Name +
      " implements " +
      exportInformation.Name +
      " {\n";
    content += this.propsClassBlock(exportInformation.Properties);
    content += this.strigifyContstuctors(exportInformation.Constructors);
    content += this.stringifyClassMethods(exportInformation.Methods);
    content += "\n}\n";

    return new DefaultFile(
      this.toFileName(exportInformation.Constructors[0].Name),
      content
    );
  }

  CreateViewFile(exportInformation: ViewExportInformation): IFile {
    let content: string = this.GetPackageString(exportInformation);
    content += this.stringifyImports(exportInformation.Imports);
    content += "\npublic class " + exportInformation.Name + " {\n";
    content +=
      "\n\tpublic " +
      new DefaultType(exportInformation.Name).BaseType +
      this.stringifyParams(exportInformation.Depdencies) +
      " {  }";

    content += "\n}\n";
    return new DefaultFile(this.toFileName(exportInformation.Name), content);
  }

  GetPackageString(exportInformation: ExportInformation): string {
    return (
      "package " +
      this.diagramName +
      "." +
      exportInformation.VerticalDimension +
      "." +
      exportInformation.HorizontalDimension +
      ";\n"
    ).toLowerCase();
  }

  private propsInterfaceBlock(props: Property[]): string {
    if (props.length === 0) {
      return "";
    }
    return (
      "\n\t" +
      props
        .map((p) => p.ReturnType.FullType + " get" + p.Name + "();")
        .join("\n\t") +
      "\n"
    );
  }

  private propsClassBlock(props: Property[]): string {
    if (props.length === 0) {
      return "";
    }
    return (
      "\n" +
      props
        .map((p) => {
          const lowerCaseName: string =
            p.Name[0].toLowerCase() + p.Name.substr(1);
          return (
            "\tprivate " +
            p.ReturnType.FullType +
            " " +
            lowerCaseName +
            ";\n\tpublic " +
            p.ReturnType.FullType +
            " get" +
            p.Name +
            "() { return " +
            lowerCaseName +
            "; }" +
            ";\n"
          );
        })
        .join("\n") +
      "\n"
    );
  }

  private stringifyImports(imports: ImportInformation[]): string {
    return (
      "\n\n" +
      imports
        .map((i) => {
          let horizontaldimension: string = i.HorizontalDimension.toLowerCase();
          if (i.Type === ElementType.Interface) {
            horizontaldimension = "interfaces";
          }
          return (
            "import " +
            this.diagramName +
            "." +
            i.VerticalDimension.toLowerCase() +
            "." +
            horizontaldimension +
            ";"
          );
        })
        .filter(ArrayHelper.UniqueFilter)
        .join("\n") +
      "\n\n"
    );
  }

  private toFileName(name: string): string {
    return new DefaultType(name).BaseType + this.fileExtension;
  }

  private stringifyInterfaceMethods(methods: Method[]): string {
    return (
      "\n" +
      methods
        .map(
          (m) =>
            "\t" +
            m.ReturnType.FullType +
            " " +
            m.Name +
            this.stringifyParams(m.ParamTypes) +
            ";"
        )
        .join("\n") +
      "\n"
    );
  }

  private stringifyClassMethods(methods: Method[]): string {
    return (
      "\n" +
      methods
        .map(
          (m) =>
            "\n\tpublic " +
            m.ReturnType.FullType +
            " " +
            m.Name +
            this.stringifyParams(m.ParamTypes) +
            " {\n\t\tthrow new NotImplementedException();\n\t}\n"
        )
        .join("\n") +
      "\n"
    );
  }

  private strigifyContstuctors(ctors: Constructor[]): string {
    return (
      "\n" +
      ctors
        .map(
          (c) => "\t" + c.Name + this.stringifyParams(c.ParamTypes) + " {  }\n"
        )
        .join("\n") +
      "\n"
    );
  }

  private stringifyParams(params: Type[]) {
    const usedParamNames: string[] = params.map((p) => p.BaseType);
    const stringifiedParams: string[] = params.map((p) => {
      let baseName: string = p.BaseType[0].toLowerCase() + p.BaseType.substr(1);
      let paramName: string = baseName;
      for (let i: number = 1; usedParamNames.includes(paramName); i++) {
        paramName = baseName + i;
      }
      usedParamNames.push(paramName);
      let param: string = p.FullType + " " + paramName;
      if (p.IsOptional) {
        param += " = null";
      }
      return param;
    });
    return "(" + stringifiedParams.join(", ") + ")";
  }
}
